<script setup lang="ts">
import type { DropdownStringOption } from '@/types/inputs'

import { ref, watch, computed, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

import { ResourceResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import useForm from '@/hooks/use-form'
import { TransportObjectType } from '@/types/transport-object'
import { Tour } from '@/types/delivery-management'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { emitter } from '@/utils/mitt'

import MyModal from '@/components/my-components/MyModal.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyButton from '@/components/my-components/MyButton.vue'

interface Props {
    modelValue: boolean
    tourId: uuid
}

type TransportObjectOption = DropdownStringOption & { type: TransportObjectType }

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void; (e: 'saved'): void }>()

const { t } = useI18n()
const auth = useAuthStore()

const vehicleIds = ref<uuid[]>([])
const unitIds = ref<uuid[]>([])
const currentVehicleIds = ref<uuid[]>([])
const currentUnitIds = ref<uuid[]>([])
const transportObjects = useLazyLoadedList<TransportObjectOption>(fetchTransportObjects)

const { data, submit, errors, loading } = useForm<{ transportObjectIds: uuid[] }>({
    transportObjectIds: [],
})

const vehicleOptions = computed(() =>
    transportObjects.items.value.filter((o) => o.type === TransportObjectType.Vehicle),
)

const unitOptions = computed(() =>
    transportObjects.items.value.filter((o) => o.type === TransportObjectType.Unit),
)

async function fetchTransportObjects(): Promise<TransportObjectOption[]> {
    if (!auth.companyId) return []
    const { data } = await axios.get<
        { id: uuid; registrationNumber: string; type: TransportObjectType }[]
    >(window.route('minimal.companies.transport-objects', { company: auth.companyId }))

    return data.map((o) => ({
        value: o.id,
        label: o.registrationNumber,
        type: o.type,
    }))
}

async function fetchTourAndPrefill() {
    const res = await axios.get<ResourceResponse<Tour>>(
        window.route('dm.company.tours.show', {
            company: auth.companyId,
            tour: props.tourId,
        }),
    )

    const tour = res.data.data
    const tos = tour.transportObjects ?? []

    currentVehicleIds.value = tos
        .filter((to) => to.type === TransportObjectType.Vehicle)
        .map((to) => to.id)
    currentUnitIds.value = tos
        .filter((to) => to.type === TransportObjectType.Unit)
        .map((to) => to.id)

    vehicleIds.value = [...currentVehicleIds.value]
    unitIds.value = [...currentUnitIds.value]
}

async function onSubmit() {
    data.transportObjectIds = [...vehicleIds.value, ...unitIds.value]
    const url = window.route('dm.company.tours.transport-objects.update', {
        company: auth.companyId,
        tour: props.tourId,
    })
    const response = await submit('PUT', url)

    if (response !== undefined) {
        emitter.emit('fetchTours')
        emit('update:modelValue', false)
        emit('saved')
    }
}

function onOpen() {
    if (!props.modelValue) return
    vehicleIds.value = []
    unitIds.value = []
    transportObjects.fetch()
    fetchTourAndPrefill()
}

onBeforeMount(() => {
    if (props.modelValue) onOpen()
})

watch(
    () => props.modelValue,
    () => onOpen(),
)
</script>

<template>
    <MyModal :value="props.modelValue" @close="emit('update:modelValue', false)">
        <template #title>{{ t('assignVehiclesAndUnits') }}</template>

        <LoaderWrapper :visible="loading" immediate />

        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <div class="flex w-full gap-3">
                <MySelect
                    v-model="vehicleIds"
                    multiple
                    searchable
                    clear-button
                    class="w-full"
                    :label="t('vehicles')"
                    :options="vehicleOptions"
                    :loading="transportObjects.fetching.value"
                    @focus="transportObjects.fetch()"
                />

                <MySelect
                    v-model="unitIds"
                    multiple
                    searchable
                    clear-button
                    class="w-full"
                    :label="t('units')"
                    :options="unitOptions"
                    :loading="transportObjects.fetching.value"
                    @focus="transportObjects.fetch()"
                />
            </div>

            <div class="mt-4 flex justify-end">
                <MyButton scheme="primary" :disabled="loading" v-text="t('save')" />
            </div>
        </MyForm>
    </MyModal>
</template>
